import { Component, Inject, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DocumentService } from 'src/app/core/services/document.service';
import {ToastrService} from 'ngx-toastr';
import { Message } from 'src/app/core/class/message';
import { WarningComponent } from 'src/app/shared/components/warning/warning.component';
import { BlockUI, NgBlockUI } from 'ng-block-ui';

export interface SmsEventRow {
    stt: number;
    code: string;
    businessName: string;
    smsContent: string;
    sendTarget: string;
    selected: boolean;
  }

  export interface ChooseSmsEventDialogData {
    objectType: 'internal' | 'customer';
    businessType: string;        // ví dụ 'SCONTRACT'
    currentSelected: string[]; // danh sách code SMS đã chọn trước đó
    applyType: string;
    functionType: string;
    serviceTypeId: string;  
    isConfigured: boolean;  
  }

@Component({
  selector: 'app-choose-sms-event-dialog',
  templateUrl: './choose-sms-event-dialog.component.html',
  styleUrls: ['./choose-sms-event-dialog.component.scss']
})
export class ChooseSmsEventDialogComponent implements OnInit{
  searchBusinessName: string = '';
  searchSmsContent: string = '';
  isConfigured?: boolean;
  displayedColumns: string[] = ['stt', 'businessName', 'smsContent', 'sendTarget', 'select'];

  // allRows: toàn bộ dữ liệu đã tải từ BE (tối đa FETCH_SIZE dòng)
  allRows: SmsEventRow[] = [];
  // displayedRows: các dòng của trang hiện tại (cắt từ allRows ở FE)
  displayedRows: SmsEventRow[] = [];
  dataSource: SmsEventRow[] = [];

  // Số dòng lấy từ BE 1 lần, sau đó phân trang hoàn toàn ở FE
  private readonly FETCH_SIZE = 100;

  // Theo dõi các code đã chọn xuyên suốt tất cả các trang (allRows bị ghi đè mỗi lần loadTemplates)
  selectedCodesSet: Set<string> = new Set<string>();
  // Baseline để so sánh "có thay đổi chưa" - snapshot data.currentSelected lúc mở popup
  baselineSelectedCodesSet: Set<string> = new Set<string>();
  // Lưu lại thông tin đầy đủ của các dòng đã từng fetch, để onSave() trả đủ dữ liệu
  // cho các code đã chọn kể cả khi không còn nằm trong allRows của lần tải hiện tại
  rowsMap: Map<string, SmsEventRow> = new Map<string, SmsEventRow>();
  // Cấu hình lần đầu (chưa có lựa chọn nào được lưu trước đó)
  isFirstTimeConfig: boolean = false;
  // Có sự thay đổi lựa chọn so với lúc mở popup (để màn bên ngoài bật nút lưu)
  changed: boolean = false;

  paginate: Paginate;

  loading = false;

  isSelectAll: boolean = false;
  @BlockUI() blockUI: NgBlockUI;

  constructor(
    public dialogRef: MatDialogRef<ChooseSmsEventDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ChooseSmsEventDialogData,
    private documentService: DocumentService,
    private message: Message,
    private dialog: MatDialog,
    private toastr: ToastrService,
  ) {
    this.paginate = {
      currentPage: 0,
      pageSize: 10,
      totalRow: 0,
      totalPage: 0,
      totalPageArray: [],
      no: []
    };
    this.selectedCodesSet = new Set<string>(this.data.currentSelected || []);
    this.baselineSelectedCodesSet = new Set<string>(this.data.currentSelected || []);
    this.isFirstTimeConfig = !this.data.isConfigured &&
  (!this.data.currentSelected || this.data.currentSelected.length === 0);
  }


  ngOnInit(): void {
    this.loadTemplates();
  }

  loadTemplates(): void {
    this.loading = true;

    // BE nhận dữ liệu qua body (POST).
    // Luôn lấy từ trang 0 với FETCH_SIZE dòng, việc phân trang do FE đảm nhiệm.
    // TODO: xác nhận với BE key "smsTemplateIds" dùng để làm gì (mình đang tạm truyền
    // currentSelected vào đây theo đúng field trong sample body bạn gửi - nếu ý nghĩa
    // khác thì cần đổi lại nguồn dữ liệu cho đúng)
    const body: any = {
      businessType: this.data.businessType,
      functionType: this.data.functionType,
      applyType: Number(this.data.applyType),
      serviceTypeId: this.data.serviceTypeId || '',
      page: 0,
      pageSize: this.FETCH_SIZE
    };

    if (this.data.currentSelected && this.data.currentSelected.length > 0) {
      body.smsTemplateIds = this.data.currentSelected;
    }

    if (this.searchBusinessName) {
      body.name = this.searchBusinessName;
    }
    if (this.searchSmsContent) {
      body.content = this.searchSmsContent;
    }
    this.blockUI.start();
    this.documentService.getListSms(body).subscribe({
      next: (res: any) => {
        this.blockUI.stop();
        // Response thật có dạng { success, data: { content, total, pageable } }
        const list = (res && res.success && res.data && res.data.content) ? res.data.content : [];

        this.allRows = list.map((item: any, index: number) => {
          // Cấu hình lần đầu: dòng nào chưa từng thấy thì tự chọn.
          // Dòng đã từng thấy (đã có trong rowsMap) thì giữ đúng trạng thái user đã chọn/bỏ chọn.
          const isNewRow = !this.rowsMap.has(item.code);
          const applyDefault = this.isFirstTimeConfig && isNewRow;

          if (applyDefault) {
            this.selectedCodesSet.add(item.code);
            // Kèm baseline để việc "tự chọn tất cả" này không bị tính là thay đổi chưa lưu
            this.baselineSelectedCodesSet.add(item.code);
          }

          return {
            stt: index + 1,
            code: item.code,
            businessName: item.name,
            smsContent: item.content,
            sendTarget: this.data.objectType === 'internal'
              ? this.message.getMessage("config_enterprise.internal")
              : this.message.getMessage("config_enterprise.external"),
            selected: this.selectedCodesSet.has(item.code)
          } as SmsEventRow;
        });

        // Lưu lại đầy đủ dữ liệu các dòng vừa fetch để dùng khi Lưu (kể cả dòng ở trang khác)
        this.allRows.forEach(row => this.rowsMap.set(row.code, row));

        // Dữ liệu mới tải về luôn bắt đầu hiển thị từ trang đầu
        // (updatePaging sẽ tự tính totalRow = allRows.length và cắt dữ liệu theo trang)
        this.updatePaging();
        this.changed = this.hasUnsavedChanges();
        this.loading = false;
      },
      error: () => {
        this.blockUI.stop();
        this.loading = false;
      }
    });
  }

  get dialogTitle(): string {
    if (this.data.functionType === 'EXPIRY_REMINDER') {
      // Nhắc hết hạn là câu hoàn chỉnh, không tách nội bộ/khách hàng
      return this.message.getMessage('messages.title_outdate');
    }

    const targetText = this.data.objectType === 'internal'
      ? this.message.getMessage('messages.target_internal_user')
      : this.message.getMessage('messages.target_customer');

    const baseKey = this.data.functionType === 'REMINDER'
      ? 'messages.title_remind'
      : 'messages.title_process';

    return this.message.getMessage(baseKey) + targetText;
  }

  onSearch(): void {
    // Mỗi lần tìm kiếm luôn quay về trang đầu và gọi lại API để BE lọc
    this.paginate.currentPage = 0;
    this.loadTemplates();
  }

  trimSearchBusinessName(): void {
    this.searchBusinessName = (this.searchBusinessName || '').trim();
  }

  trimSearchSmsContent(): void {
    this.searchSmsContent = (this.searchSmsContent || '').trim();
  }

  updatePaging(): void {
    // allRows chứa toàn bộ dữ liệu đã tải (tối đa FETCH_SIZE dòng) -> phân trang ở FE
    this.paginate.totalRow = this.allRows.length;
    this.paginate.totalPage = this.paginate.totalRow > 0
      ? Math.ceil(this.paginate.totalRow / this.paginate.pageSize)
      : 0;
    if (this.paginate.currentPage >= this.paginate.totalPage) {
      this.paginate.currentPage = this.paginate.totalPage - 1;
    }
    if (this.paginate.currentPage < 0) {
      this.paginate.currentPage = 0;
    }
    this.paginate.totalPageArray = this.paginate.totalPage > 0
      ? this.counter(this.paginate.totalPage, 1)
      : [];

    // Cắt đúng phần dữ liệu của trang hiện tại
    const start = this.paginate.currentPage * this.paginate.pageSize;
    this.displayedRows = this.allRows.slice(start, start + this.paginate.pageSize);
    this.dataSource = this.displayedRows;
    this.syncSelectAllState();
  }

  counter(index: number, from: number): any {
    return new Array(index).fill(0).map((x, i) => i + from);
  }

  onChangePage(e: any) {
    this.paginate.currentPage = e.page ? e.page : 0;
    this.paginate.pageSize = e.pageSize;
    // Đổi trang / đổi số dòng mỗi trang chỉ cắt lại dữ liệu ở FE, không gọi API
    this.updatePaging();
  }

  toggleSelectAll(): void {
    // Chỉ áp dụng cho các dòng của trang đang hiển thị
    this.displayedRows.forEach(row => {
      row.selected = this.isSelectAll;
      if (this.isSelectAll) {
        this.selectedCodesSet.add(row.code);
      } else {
        this.selectedCodesSet.delete(row.code);
      }
    });
    this.changed = this.hasUnsavedChanges();
  }

  syncSelectAllState(): void {
    this.isSelectAll = this.displayedRows.length > 0 &&
      this.displayedRows.every(row => row.selected);
  }

  onRowCheckChange(row: SmsEventRow): void {
    if (row.selected) {
      this.selectedCodesSet.add(row.code);
    } else {
      this.selectedCodesSet.delete(row.code);
    }
    this.syncSelectAllState();
    this.changed = this.hasUnsavedChanges();
  }

  private hasUnsavedChanges(): boolean {
    if (this.baselineSelectedCodesSet.size !== this.selectedCodesSet.size) {
      return true;
    }
    for (const code of this.selectedCodesSet) {
      if (!this.baselineSelectedCodesSet.has(code)) {
        return true;
      }
    }
    return false;
  }

  onCancel(): void {
    if (!this.hasUnsavedChanges()) {
      this.dialogRef.close(null);
      return;
    }

    // TODO: chỉnh lại nội dung message cho đúng yêu cầu/chuẩn dịch (translate key) của dự án
    const confirmDialog = this.dialog.open(WarningComponent, {
      data: 'messages.confirm_change_data'
    });

    confirmDialog.afterClosed().subscribe((result: boolean) => {
      if (result === true) {
        // Đồng ý: đóng popup xác nhận, đóng luôn dialog chọn SMS, bỏ hết thay đổi
        this.dialogRef.close(null);
      }
      // Hủy (false) hoặc đóng bằng nút X: ở lại dialog chọn SMS, giữ nguyên lựa chọn đang thao tác
    });
  }

  get hasSelectedRows(): boolean {
    return this.selectedCodesSet.size > 0;
  }

  onSave(): void {
    const allRowsResult: SmsEventRow[] = Array.from(this.rowsMap.values())
      .map(row => ({
        ...row,
        selected: this.selectedCodesSet.has(row.code)
      }));
  
    console.table(allRowsResult.map(r => ({ code: r.code, selected: r.selected })));
  
    this.toastr.success(this.message.getMessage('messages.save_success_sms'));
    this.dialogRef.close({ rows: allRowsResult, changed: this.changed });
  }
}

export class Paginate {
  currentPage: number = 0;
  pageSize: number = 10;
  totalRow: number = 0;
  totalPage: number = 0;
  totalPageArray: number[] = [];
  no: number[] = [];
}